import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import { Layers, Map, MapPin, RefreshCw, X } from 'lucide-react';
import L from 'leaflet';
import supabase from '../../../config/supabaseClient';

// Configurar ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-shadow.png',
});

// Função para criar ícones personalizados baseados no status
const criarIconePersonalizado = (status) => {
  const cor = getCorPorStatus(status);

  return L.divIcon({
    className: 'obra-fixa-marker',
    html: `<div style="background-color: ${cor}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.5);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8]
  });
};

// Função auxiliar para determinar a cor baseada no status
function getCorPorStatus(status) {
  switch (status) {
    case 'Planejada':
      return '#ffc107'; // Amarelo
    case 'Em execução':
      return '#28a745'; // Verde
    case 'Finalizada':
      return '#007bff'; // Azul
    default:
      return '#6c757d'; // Cinza
  }
}

const MapaIntegradoPublico = () => {
  const [mostrarObras, setMostrarObras] = useState(true);
  const [mostrarObrasFixas, setMostrarObrasFixas] = useState(true);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState({
    'Planejada': true,
    'Em execução': true,
    'Finalizada': true
  });
  const [obraSelecionada, setObraSelecionada] = useState(null);
  const [obraFixaSelecionada, setObraFixaSelecionada] = useState(null);
  const [dadosCarregados, setDadosCarregados] = useState({
    obras: [],
    obrasFixas: []
  });
  const [carregando, setCarregando] = useState(true);
  const intervalRef = useRef(null);
  const ultimaAtualizacao = useRef(Date.now());

  // Coordenadas de Conceição do Castelo - ES
  const centroConceicaoCastelo = [-20.359292, -41.247282];

  // Função para buscar dados do Supabase
  const buscarDados = async (forceUpdate = false) => {
    try {
      // Verificar se passou pelo menos 30 segundos desde a última atualização
      const agora = Date.now();
      if (!forceUpdate && agora - ultimaAtualizacao.current < 30000) {
        console.log("Ignorando atualização, última atualização foi há menos de 30 segundos");
        return; // Não atualizar se passou menos de 30 segundos e não for forçado
      }

      setCarregando(true);
      console.log("Buscando dados públicos...");
      console.log("URL do Supabase:", process.env.REACT_APP_SUPABASE_URL);

      // Buscar obras - apenas dados públicos
      console.log("Iniciando busca de obras...");
      const { data: obrasData, error: obrasError } = await supabase
        .from('obras')
        .select('id, nome, status, data_inicio, data_previsao_termino');

      console.log("Resultado da busca de obras:", { data: obrasData, error: obrasError });

      if (obrasError) {
        console.error("Erro ao buscar obras:", obrasError);
      } else if (obrasData) {
        console.log(`Encontradas ${obrasData.length} obras`);

        // Para cada obra, buscar seus trechos (sem derivações para simplificar)
        const obrasComTrechos = await Promise.all(obrasData.map(async (obra) => {
          console.log(`Buscando trechos para obra ${obra.id} - ${obra.nome}`);

          // Buscar trechos da obra
          const { data: trechosData, error: trechosError } = await supabase
            .from('trechos')
            .select('id, nome, pontos')
            .eq('obra_id', obra.id);

          if (trechosError) {
            console.error(`Erro ao buscar trechos para obra ${obra.id}:`, trechosError);
            return {
              id: obra.id,
              nome: obra.nome,
              status: obra.status,
              dataInicio: obra.data_inicio,
              dataPrevisaoTermino: obra.data_previsao_termino,
              trechos: []
            };
          }

          console.log(`Encontrados ${trechosData ? trechosData.length : 0} trechos para obra ${obra.id}`);

          // Processar trechos
          const trechosProcessados = (trechosData || []).map(trecho => {
            try {
              const pontos = trecho.pontos ? JSON.parse(trecho.pontos) : [];
              console.log(`Trecho ${trecho.id} tem ${pontos.length} pontos`);
              return {
                id: trecho.id,
                nome: trecho.nome,
                pontos: pontos
              };
            } catch (error) {
              console.error(`Erro ao processar pontos do trecho ${trecho.id}:`, error);
              return {
                id: trecho.id,
                nome: trecho.nome,
                pontos: []
              };
            }
          });

          // Retornar obra com trechos, convertendo snake_case para camelCase
          return {
            id: obra.id,
            nome: obra.nome,
            status: obra.status,
            dataInicio: obra.data_inicio,
            dataPrevisaoTermino: obra.data_previsao_termino,
            trechos: trechosProcessados
          };
        }));

        console.log("Obras processadas com trechos:", obrasComTrechos);

        // Atualizar o estado com os dados
        setDadosCarregados(prev => {
          console.log("Atualizando estado com obras:", obrasComTrechos.length);
          return {
            ...prev,
            obras: obrasComTrechos
          };
        });
      }

      // Buscar obras fixas - apenas dados públicos
      console.log("Iniciando busca de obras fixas...");
      const { data: obrasFixasData, error: obrasFixasError } = await supabase
        .from('obras_fixas')
        .select('id, nome, status, data_inicio, data_previsao_termino, latitude, longitude, nome_local');

      console.log("Resultado da busca de obras fixas:", { data: obrasFixasData, error: obrasFixasError });

      if (obrasFixasError) {
        console.error("Erro ao buscar obras fixas:", obrasFixasError);
      } else if (obrasFixasData) {
        console.log(`Encontradas ${obrasFixasData.length} obras fixas`);

        // Processar obras fixas
        const obrasFixasProcessadas = obrasFixasData.map(obraFixa => {
          console.log(`Processando obra fixa ${obraFixa.id} - ${obraFixa.nome} - Coordenadas: ${obraFixa.latitude}, ${obraFixa.longitude}`);
          return {
            id: obraFixa.id,
            nome: obraFixa.nome,
            status: obraFixa.status,
            dataInicio: obraFixa.data_inicio,
            dataPrevisaoTermino: obraFixa.data_previsao_termino,
            localizacao: {
              latitude: obraFixa.latitude,
              longitude: obraFixa.longitude,
              nome: obraFixa.nome_local
            }
          };
        });

        console.log("Obras fixas processadas:", obrasFixasProcessadas);

        // Atualizar o estado com os dados
        setDadosCarregados(prev => {
          console.log("Atualizando estado com obras fixas:", obrasFixasProcessadas.length);
          return {
            ...prev,
            obrasFixas: obrasFixasProcessadas
          };
        });
      }

      ultimaAtualizacao.current = agora;
      console.log("Dados públicos carregados com sucesso!");
      console.log("Estado final de dadosCarregados:", {
        obras: dadosCarregados.obras.length,
        obrasFixas: dadosCarregados.obrasFixas.length
      });
    } catch (error) {
      console.error("Erro ao buscar dados públicos:", error);
    } finally {
      setCarregando(false);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    console.log("MapaIntegradoPublico - useEffect executado");

    // Forçar busca imediata de dados com parâmetro forceUpdate=true
    buscarDados(true);

    // Configurar atualização periódica
    intervalRef.current = setInterval(() => {
      console.log("Executando atualização periódica");
      buscarDados();
    }, 300000); // Atualizar a cada 5 minutos

    return () => {
      console.log("MapaIntegradoPublico - useEffect cleanup");
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Verificar e filtrar obras por status
  const obrasValidas = dadosCarregados.obras.filter(obra => {
    if (!obra) {
      console.error("Obra inválida encontrada:", obra);
      return false;
    }
    if (!obra.status) {
      console.error("Obra sem status encontrada:", obra);
      return false;
    }
    if (!filtroStatus[obra.status]) {
      console.log(`Obra ${obra.id} - ${obra.nome} filtrada por status: ${obra.status}`);
      return false;
    }
    if (!obra.trechos || !Array.isArray(obra.trechos) || obra.trechos.length === 0) {
      console.warn(`Obra ${obra.id} - ${obra.nome} sem trechos válidos`);
      // Ainda retornamos true para mostrar a obra, mesmo sem trechos
      return true;
    }
    return true;
  });

  const obrasFixasValidas = dadosCarregados.obrasFixas.filter(obraFixa => {
    if (!obraFixa) {
      console.error("Obra fixa inválida encontrada:", obraFixa);
      return false;
    }
    if (!obraFixa.status) {
      console.error("Obra fixa sem status encontrada:", obraFixa);
      return false;
    }
    if (!filtroStatus[obraFixa.status]) {
      console.log(`Obra fixa ${obraFixa.id} - ${obraFixa.nome} filtrada por status: ${obraFixa.status}`);
      return false;
    }
    if (!obraFixa.localizacao || !obraFixa.localizacao.latitude || !obraFixa.localizacao.longitude) {
      console.warn(`Obra fixa ${obraFixa.id} - ${obraFixa.nome} sem localização válida`);
      return false;
    }
    return true;
  });

  const obrasFiltradasPorStatus = obrasValidas;
  const obrasFixasFiltradasPorStatus = obrasFixasValidas;

  // Formatar data
  const formatarData = (data) => {
    if (!data) return 'Não definida';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  // Componente de botão de atualização
  const AtualizarButton = () => {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async () => {
      setIsLoading(true);
      await buscarDados(true); // Forçar atualização
      setTimeout(() => setIsLoading(false), 1000);
    };

    return (
      <button
        onClick={handleClick}
        disabled={isLoading}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
          backgroundColor: 'white',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          border: 'none',
          cursor: isLoading ? 'default' : 'pointer'
        }}
        title="Atualizar dados do mapa"
      >
        <RefreshCw
          size={20}
          color="#333"
          className={isLoading ? 'rotating' : ''}
          style={{
            animation: isLoading ? 'spin 1s linear infinite' : 'none'
          }}
        />
      </button>
    );
  };

  // Componente de botão de depuração
  const DebugButton = () => {
    const handleClick = () => {
      console.log("=== INFORMAÇÕES DE DEPURAÇÃO ===");
      console.log("URL do Supabase:", process.env.REACT_APP_SUPABASE_URL);
      console.log("Estado atual:", {
        mostrarObras,
        mostrarObrasFixas,
        filtroStatus,
        dadosCarregados,
        obrasFiltradasPorStatus,
        obrasFixasFiltradasPorStatus
      });

      // Tentar uma consulta simples para verificar a conexão
      supabase
        .from('obras')
        .select('count')
        .then(({ data, error }) => {
          console.log("Teste de conexão com obras:", { data, error });
        });

      supabase
        .from('obras_fixas')
        .select('count')
        .then(({ data, error }) => {
          console.log("Teste de conexão com obras_fixas:", { data, error });
        });

      alert("Informações de depuração enviadas para o console. Pressione F12 para visualizar.");
    };

    return (
      <button
        onClick={handleClick}
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          zIndex: 1000,
          backgroundColor: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          border: 'none',
          cursor: 'pointer',
          fontSize: '12px'
        }}
        title="Informações de depuração"
      >
        Debug
      </button>
    );
  };

  // Removido o componente TestDataButton pois não é necessário

  console.log("Renderizando MapaIntegradoPublico");
  console.log("Estado atual:", {
    mostrarObras,
    mostrarObrasFixas,
    filtroStatus,
    dadosCarregados: {
      obras: dadosCarregados.obras.length,
      obrasFixas: dadosCarregados.obrasFixas.length
    },
    obrasFiltradasPorStatus: obrasFiltradasPorStatus.length,
    obrasFixasFiltradasPorStatus: obrasFixasFiltradasPorStatus.length,
    carregando
  });

  // Se não temos dados e não estamos carregando, forçar uma busca
  useEffect(() => {
    if (dadosCarregados.obras.length === 0 && dadosCarregados.obrasFixas.length === 0 && !carregando) {
      console.log("Não há dados carregados e não estamos carregando. Forçando busca de dados...");
      buscarDados(true);
    }
  }, [dadosCarregados.obras.length, dadosCarregados.obrasFixas.length, carregando]);

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      position: 'relative'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .rotating {
            animation: spin 1s linear infinite;
          }
        `}
      </style>

      {/* Cabeçalho */}
      <div style={{
        padding: '10px 15px',
        backgroundColor: '#3f51b5',
        color: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        zIndex: 30000,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '18px' }}>Mapa de Obras Públicas</h1>
          <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>Prefeitura Teste</p>
        </div>
        <div style={{ fontSize: '12px' }}>
          Última atualização: {new Date(ultimaAtualizacao.current).toLocaleString()}
        </div>
      </div>

      <MapContainer
        center={centroConceicaoCastelo}
        zoom={13}
        style={{ height: '100vh', width: '100%', paddingTop: '60px' }}
        whenCreated={(map) => {
          console.log("Mapa criado:", map);
        }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {/* Renderizar obras lineares (trechos) */}
        {mostrarObras && obrasFiltradasPorStatus.map(obra => {
          console.log(`Renderizando obra ${obra.id} - ${obra.nome} com ${obra.trechos ? obra.trechos.length : 0} trechos`);
          return (
            <React.Fragment key={obra.id}>
              {obra.trechos && obra.trechos.map((trecho, index) => {
                console.log(`Renderizando trecho ${trecho.id} com ${trecho.pontos ? trecho.pontos.length : 0} pontos`);
                return (
                  <Polyline
                    key={`${obra.id}-${index}`}
                    positions={trecho.pontos}
                    pathOptions={{
                      color: getCorPorStatus(obra.status),
                      weight: 5,
                      opacity: 0.7
                    }}
                    eventHandlers={{
                      click: () => setObraSelecionada(obra)
                    }}
                  />
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Renderizar obras fixas (pontos) */}
        {mostrarObrasFixas && obrasFixasFiltradasPorStatus.map(obraFixa => {
          console.log(`Renderizando obra fixa ${obraFixa.id} - ${obraFixa.nome} em ${obraFixa.localizacao?.latitude}, ${obraFixa.localizacao?.longitude}`);
          return obraFixa.localizacao && obraFixa.localizacao.latitude && obraFixa.localizacao.longitude && (
            <Marker
              key={obraFixa.id}
              position={[obraFixa.localizacao.latitude, obraFixa.localizacao.longitude]}
              icon={criarIconePersonalizado(obraFixa.status)}
              eventHandlers={{
                click: () => setObraFixaSelecionada(obraFixa)
              }}
            >
              <Popup>
                <div style={{ minWidth: '200px' }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{obraFixa.nome}</h4>
                  {obraFixa.localizacao.nome && (
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Local:</strong> {obraFixa.localizacao.nome}
                    </p>
                  )}
                  <p style={{ margin: '5px 0', fontSize: '14px' }}>
                    <strong>Status:</strong> <span style={{ color: getCorPorStatus(obraFixa.status) }}>{obraFixa.status}</span>
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Botão para mostrar/ocultar filtros */}
      <button
        onClick={() => setMostrarFiltros(!mostrarFiltros)}
        style={{
          position: 'fixed',
          top: '70px',
          right: '10px',
          zIndex: 15000,
          backgroundColor: 'white',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        <Layers size={20} color="#333" />
      </button>

      {/* Painel de filtros */}
      {mostrarFiltros && (
        <div style={{
          position: 'fixed',
          top: '120px',
          right: '10px',
          zIndex: 15000,
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '15px',
          width: '250px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>Filtros</h3>
            <button
              onClick={() => setMostrarFiltros(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '5px'
              }}
            >
              <X size={16} color="#666" />
            </button>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Tipos de Obras</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={mostrarObras}
                  onChange={() => setMostrarObras(!mostrarObras)}
                />
                <Map size={16} style={{ color: '#667eea' }} />
                <span>Obras Lineares</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={mostrarObrasFixas}
                  onChange={() => setMostrarObrasFixas(!mostrarObrasFixas)}
                />
                <MapPin size={16} style={{ color: '#9c27b0' }} />
                <span>Obras Fixas</span>
              </label>
            </div>
          </div>

          <div>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Status</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filtroStatus['Planejada']}
                  onChange={() => setFiltroStatus({ ...filtroStatus, 'Planejada': !filtroStatus['Planejada'] })}
                />
                <span style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#ffc107',
                  borderRadius: '50%',
                  display: 'inline-block'
                }}></span>
                <span>Planejada</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filtroStatus['Em execução']}
                  onChange={() => setFiltroStatus({ ...filtroStatus, 'Em execução': !filtroStatus['Em execução'] })}
                />
                <span style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#28a745',
                  borderRadius: '50%',
                  display: 'inline-block'
                }}></span>
                <span>Em execução</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filtroStatus['Finalizada']}
                  onChange={() => setFiltroStatus({ ...filtroStatus, 'Finalizada': !filtroStatus['Finalizada'] })}
                />
                <span style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#007bff',
                  borderRadius: '50%',
                  display: 'inline-block'
                }}></span>
                <span>Finalizada</span>
              </label>
            </div>
          </div>

          <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
            <p style={{ margin: '0' }}>
              Total: {mostrarObras ? obrasFiltradasPorStatus.length : 0} obras lineares, {' '}
              {mostrarObrasFixas ? obrasFixasFiltradasPorStatus.length : 0} obras fixas
            </p>
          </div>

          <div style={{ marginTop: '10px', fontSize: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', backgroundColor: '#ffc107', borderRadius: '50%', display: 'inline-block' }}></span>
                <span>Planejada</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', backgroundColor: '#28a745', borderRadius: '50%', display: 'inline-block' }}></span>
                <span>Em execução</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', backgroundColor: '#007bff', borderRadius: '50%', display: 'inline-block' }}></span>
                <span>Finalizada</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalhes da obra linear */}
      {obraSelecionada && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 25000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>Detalhes da Obra</h3>
              <button
                onClick={() => setObraSelecionada(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                <X size={20} color="#666" />
              </button>
            </div>

            <h4 style={{ color: '#333', marginBottom: '15px' }}>{obraSelecionada.nome}</h4>

            <div style={{ display: 'grid', gap: '10px', marginBottom: '15px' }}>
              <div>
                <strong>Status:</strong>
                <span
                  style={{
                    color: getCorPorStatus(obraSelecionada.status),
                    fontWeight: 'bold',
                    marginLeft: '8px'
                  }}
                >
                  {obraSelecionada.status}
                </span>
              </div>
              <div>
                <strong>Data de Início:</strong> {formatarData(obraSelecionada.dataInicio)}
              </div>
              <div>
                <strong>Previsão de Término:</strong> {formatarData(obraSelecionada.dataPrevisaoTermino)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalhes da obra fixa */}
      {obraFixaSelecionada && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 25000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>Detalhes da Obra</h3>
              <button
                onClick={() => setObraFixaSelecionada(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                <X size={20} color="#666" />
              </button>
            </div>

            <h4 style={{ color: '#333', marginBottom: '15px' }}>{obraFixaSelecionada.nome}</h4>

            <div style={{ display: 'grid', gap: '10px', marginBottom: '15px' }}>
              <div>
                <strong>Status:</strong>
                <span
                  style={{
                    color: getCorPorStatus(obraFixaSelecionada.status),
                    fontWeight: 'bold',
                    marginLeft: '8px'
                  }}
                >
                  {obraFixaSelecionada.status}
                </span>
              </div>
              <div>
                <strong>Data de Início:</strong> {formatarData(obraFixaSelecionada.dataInicio)}
              </div>
              <div>
                <strong>Previsão de Término:</strong> {formatarData(obraFixaSelecionada.dataPrevisaoTermino)}
              </div>
              {obraFixaSelecionada.localizacao && obraFixaSelecionada.localizacao.nome && (
                <div>
                  <strong>Local:</strong> {obraFixaSelecionada.localizacao.nome}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Indicador de carregamento */}
      {carregando && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: '15px 20px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          zIndex: 1500,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <RefreshCw
            size={20}
            className="rotating"
            style={{
              animation: 'spin 1s linear infinite'
            }}
          />
          <span>Carregando dados...</span>
        </div>
      )}
    </div>
  );
};

export default MapaIntegradoPublico;